import { useState } from 'react';
import {
  Modal,
  FormField,
  Input,
  Box,
  SpaceBetween,
  Button,
  Alert,
} from '@amzn/awsui-components-console';
import { useAuthState } from '../../../authentication/context';
import { callApi } from '../../../client';
import {
  DeleteLaunchTaskCommand,
  DeleteLaunchTaskCommandOutput,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { NoTokenError } from '../../../errors/NoTokenError';
import { useApiErrors } from '../../../components/ApiErrorContext';
import { refreshPage } from './refreshPage';

interface DeleteProps {
  launchId: string;
  onClose: () => void;
}

export const DeleteModal = ({ launchId, onClose }: DeleteProps) => {
  const [value, setValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const { token } = useAuthState();
  const { pushError } = useApiErrors();

  async function callDeleteLaunchTaskAPI() {
    if (!token) throw new NoTokenError();

    setIsDeleting(true);
    try {
      const response: DeleteLaunchTaskCommandOutput = await callApi(
        new DeleteLaunchTaskCommand({
          launchId: launchId,
        }),
        token
      );

      const status = response.$metadata.httpStatusCode ?? 0;
      if (status >= 200 && status < 300) {
        // 2XX response: Close the modal first so the user sees the action
        // complete, then refresh the page to reflect the deletion.
        onClose();
        refreshPage();
        return;
      }

      // Non-2XX response: surface a generic error and close the modal.
      pushError(
        'Delete Failed',
        `delete ${launchId} returned status ${
          response.$metadata.httpStatusCode ?? 'unknown'
        }`
      );
      onClose();
    } catch (err: any) {
      pushError(
        'Delete Failed',
        err?.message || `delete ${launchId} operation failed`
      );
      // Close the modal so the user can see the Flashbar error.
      onClose();
    } finally {
      setIsDeleting(false);
    }
  }

  const handleCancel = () => {
    if (isDeleting) return;
    onClose();
  };

  function isConfirmed(text: string) {
    return text.toLowerCase() === 'confirm';
  }

  const handleDelete = () => {
    void callDeleteLaunchTaskAPI();
  };

  return (
    <div>
      <SpaceBetween direction='vertical' size='m'>
        <Modal
          onDismiss={handleCancel}
          visible={true}
          footer={
            <Box float='right'>
              <SpaceBetween direction='horizontal' size='xs'>
                <Button
                  variant='link'
                  onClick={handleCancel}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  disabled={!isConfirmed(value) || isDeleting}
                  loading={isDeleting}
                  variant='primary'
                  onClick={handleDelete}
                  data-test-id='confirm-delete-task-button'
                >
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
          header='Delete Confirmation'
        >
          <FormField
            label='Are you sure you want to delete this Launch with ID?'
            description=''
          >
            <Input value={launchId} readOnly />
          </FormField>
          <Alert statusIconAriaLabel='Warning' type='warning'>
            Proceeding with this action will mark this launch item as deleted,
            and assignees will not be able to see or receive notifications for
            this task
          </Alert>
          <span>
            To avoid accidental deletion, we ask you to provide additional
            written consent
          </span>
          <br></br>
          <span>
            <b>Type confirm this deletion, type "confirm"</b>
          </span>

          <Input
            onChange={({ detail }) => setValue(detail.value)}
            value={value}
          />
        </Modal>
      </SpaceBetween>
    </div>
  );
};
